import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';

import { getRouteStackPath } from '@portal/config/routes';

import { translate } from '@portal/services/i18n';
import { maskPhoneInput, unmaskField } from '@portal/services/masks';
import { Col } from 'antd';

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';
import { reportFormValidationError } from '@portal/services/formValidationReporter';
import { validateEmail } from '@portal/utils/validate';
import React, { useEffect, useMemo } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { validatePhone } from 'validations-br';

type FormValues = {
  name: string;
  email: string;
  cellphone: string;
};

const buildSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(
        1,
        translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.NAME')
      )
      .refine(
        (value) => value.split(' ').filter(Boolean).length >= 2,
        translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.NAME_FULL'
        )
      ),
    email: z
      .string()
      .trim()
      .min(
        1,
        translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.EMAIL_REQUIRED'
        )
      )
      .refine(
        (value) => validateEmail(value),
        translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.EMAIL_INVALID'
        )
      ),
    cellphone: z
      .string()
      .min(
        1,
        translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.CELLPHONE_REQUIRED'
        )
      )
      .refine(
        (value) => validatePhone(value),
        translate(
          'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.VALIDATION.CELLPHONE_INVALID'
        )
      ),
  });

const AccountMultiAccess = () => {
  const navigate = useNavigation<{ account: models.AccountMultiAccess }>();
  const schema = useMemo(() => buildSchema(), []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      cellphone: '',
    },
  });

  useEffect(() => {
    const restored = navigate?.location?.state?.account;
    if (restored) {
      reset({
        name: restored.name ?? '',
        email: restored.email ?? '',
        cellphone: restored.cellphone ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: FormValues) => {
    navigate(getRouteStackPath('PROFILE', 'CREDENTIAL_MULTI_ACCESS'), {
      state: { account: data },
    });
  };

  const onInvalid = (formErrors: FieldErrors<FormValues>) => {
    reportFormValidationError('multi-access-account', formErrors);
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
            url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.TITLE'
            ),
          },
        ]}
      />
      <div className="multiaccess-page">
        <AdvancedPanelContentHeader
          testId="account-multiaccess-header"
          title={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.TITLE')}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.DESCRIPTION'
          )}
          goBackAction={() => navigate.back()}
        />
        <AdvancedCard testId="account-multiaccess-card">
          <form
            className="multiaccess-account-form"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            noValidate
          >
            <Col className="multiaccess-account-form__field">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <AdvancedInput
                    label={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.NAME'
                    )}
                    variant="outlined"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.name}
                    errorMessage={errors.name?.message}
                  />
                )}
              />
            </Col>
            <Col className="multiaccess-account-form__field">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <AdvancedInput
                    label={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.EMAIL'
                    )}
                    variant="outlined"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!errors.email}
                    errorMessage={errors.email?.message}
                  />
                )}
              />
            </Col>
            <Col className="multiaccess-account-form__field">
              <Controller
                name="cellphone"
                control={control}
                render={({ field }) => (
                  <AdvancedInput
                    label={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.CELLPHONE'
                    )}
                    variant="outlined"
                    value={maskPhoneInput(field.value)}
                    onChange={(value) => field.onChange(unmaskField(value))}
                    error={!!errors.cellphone}
                    errorMessage={errors.cellphone?.message}
                  />
                )}
              />
            </Col>
            <div className="multiaccess-account-form__actions">
              <AdvancedButton
                type="submit"
                text={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.BUTTON')}
                disabled={!isValid}
              />
            </div>
          </form>
        </AdvancedCard>
      </div>
    </>
  );
};

export default AccountMultiAccess;
